import { Button, Select, Spinner, useTranslation } from '@hbs-us/ui';
import { useId, useMemo, useState } from 'react';

import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { AdvancedAccessPanel } from './AdvancedAccessPanel.js';
import type {
  PermissionKey,
  PermissionOverrideValue,
  UserListItemDto,
  UserRole,
} from './contractsMirror.js';
import { RoleCards, RoleGrantSummary } from './RoleCards.js';
import { countOverrides } from './roleSummary.js';
import { usePermissionsMutation } from './usePermissionsMutation.js';
import { useUsersQuery } from './useUsersQuery.js';

/**
 * People & Access — pick a person, pick a role. The whole screen, for most workspaces.
 *
 * WHY THIS SHAPE. The previous surface was a 19-column × N-user permission matrix. It
 * grew in both directions and asked an owner to make 35 independent security decisions
 * per person, when production shows they make one: which of three roles this is. Across
 * 50 production users there are exactly TWO override rows, both on the same Manager, both
 * granting user management — that is a missing role, not evidence that per-key control is
 * the right primitive.
 *
 * So the fine-grained model is intact end to end (the API, the override table and all 35
 * keys are unchanged); it is just no longer the front door. It sits behind Advanced,
 * with the override count on the button so real drift stays visible.
 *
 * SECURITY POSTURE (T-22-privesc), unchanged from the matrix:
 *   - every change confirms first, through the audit-aware `ConfirmDialog`, never a
 *     native confirm and never on a stray click;
 *   - the SERVER is authoritative. It re-checks that the actor is an Admin
 *     (`requireApiAdmin`), refuses a self-role-change (`cannot_change_own_role`), enforces
 *     the ≥1-active-Admin invariant (409 `last_active_admin`) and writes the audit event.
 *     This view mirrors those rules to keep the UI honest; it does not enforce them.
 *   - no request ever carries a tenantId (SEC-11).
 */

/**
 * The only roles this screen offers. Admin is absent by design, and there is no Admin card
 * to show either: `isConfigurableRole` already keeps Admins out of the person list, so an
 * Admin is never the selected user here.
 *
 * Promotion to Admin and demotion from it are last-active-Admin hazards the server guards
 * with a 409 (`last_active_admin`). Putting them in the same one-click card as "make this
 * person a Manager" invites an accident, so the user edit screen owns that decision.
 */
const ASSIGNABLE_ROLES: readonly UserRole[] = ['Manager', 'Employee'];

function isConfigurableRole(role: UserRole): boolean {
  return role === 'Manager' || role === 'Employee';
}

/** The pending change the ConfirmDialog is asking about — a role OR a single override. */
type Pending =
  | { kind: 'role'; user: UserListItemDto; next: UserRole }
  | {
      kind: 'override';
      user: UserListItemDto;
      permission: PermissionKey;
      next: PermissionOverrideValue;
      granting: boolean;
    };

export function AccessView() {
  const { t } = useTranslation();
  const query = useUsersQuery();
  const mutation = usePermissionsMutation();
  const personSelectId = useId();

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  // Optimistic per-(user,permission) layer, cleared on settle so the server's resolved
  // state is what stays on screen.
  const [localOverrides, setLocalOverrides] = useState<Record<string, PermissionOverrideValue>>({});

  const users = useMemo(() => query.data?.users ?? [], [query.data]);
  const configurableUsers = useMemo(
    () => users.filter((user) => isConfigurableRole(user.role)),
    [users],
  );
  const rolePresets = query.data?.rolePresets ?? ({} as Record<UserRole, PermissionKey[]>);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('users:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('users:error.body')}</p>
      </section>
    );
  }

  // Falls back to the first person so the pane is never empty, and so a user who leaves
  // the directory (deactivated mid-session) does not strand the view on a missing id.
  const selectedUser =
    configurableUsers.find((user) => user.id === selectedUserId) ?? configurableUsers[0];

  function cellKey(userId: number, permission: PermissionKey): string {
    return `${userId}:${permission}`;
  }

  function isOn(user: UserListItemDto, permission: PermissionKey): boolean {
    const local = localOverrides[cellKey(user.id, permission)];
    if (local === 'allow') return true;
    if (local === 'deny') return false;
    if (local === 'default') return user.rolePermissions.includes(permission);
    return user.effectivePermissions.includes(permission);
  }

  function requestToggle(user: UserListItemDto, permission: PermissionKey) {
    const currentlyOn = isOn(user, permission);
    const enabledByRole = user.rolePermissions.includes(permission);
    setPending({
      kind: 'override',
      user,
      permission,
      // Revoking something the role grants needs an explicit 'deny'; revoking an extra
      // grant just returns it to 'default'. Same either way round.
      next: currentlyOn
        ? enabledByRole
          ? 'deny'
          : 'default'
        : enabledByRole
          ? 'default'
          : 'allow',
      granting: !currentlyOn,
    });
  }

  function requestRole(user: UserListItemDto, next: UserRole) {
    if (next === user.role) return;
    setPending({ kind: 'role', user, next });
  }

  function applyPending() {
    if (!pending) return;

    if (pending.kind === 'role') {
      mutation.mutate({ userId: pending.user.id, role: pending.next });
      setPending(null);
      return;
    }

    const { user, permission, next } = pending;
    const key = cellKey(user.id, permission);
    setLocalOverrides((prev) => ({ ...prev, [key]: next }));
    mutation.mutate(
      { userId: user.id, overrides: { [permission]: next } },
      {
        onSettled: () => {
          setLocalOverrides((prev) => {
            const rest = { ...prev };
            delete rest[key];
            return rest;
          });
        },
      },
    );
    setPending(null);
  }

  const overrideCount = selectedUser ? countOverrides(selectedUser) : 0;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t('users:access.title')}
        </h1>
        <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('users:access.subtitle')}</p>
      </header>

      {mutation.isError ? (
        <p role="alert" className="mb-4 text-destructive text-sm">
          {t('users:permissions.error')}
        </p>
      ) : null}

      {!selectedUser ? (
        <div className="rounded-md border border-border border-dashed bg-card p-16 text-center">
          <p className="text-muted-foreground text-sm">{t('users:permissions.empty')}</p>
        </div>
      ) : (
        <div className="grid min-w-0 gap-6 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
          {/* Person picker. A native <select> below md so a phone gets its own OS picker
              and a long name can never widen the page; a real list at md+. */}
          <div className="min-w-0">
            <label
              htmlFor={personSelectId}
              className="mb-1 block font-medium text-foreground text-sm md:hidden"
            >
              {t('users:permissions.personLabel')}
            </label>
            <Select
              id={personSelectId}
              value={String(selectedUser.id)}
              onChange={(event) => {
                setSelectedUserId(Number(event.target.value));
                setShowAdvanced(false);
              }}
              className="md:hidden"
            >
              {configurableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} · {t(`users:roles.${user.role}`)}
                </option>
              ))}
            </Select>

            <nav aria-label={t('users:permissions.personLabel')} className="hidden md:block">
              <ul className="overflow-hidden rounded-md border border-border bg-card">
                {configurableUsers.map((user) => {
                  const active = user.id === selectedUser.id;
                  return (
                    <li key={user.id} className="border-border border-b last:border-0">
                      <button
                        type="button"
                        aria-current={active ? 'true' : undefined}
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setShowAdvanced(false);
                        }}
                        className={`flex min-h-11 w-full flex-col items-start px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
                          active ? 'bg-primary/5' : 'hover:bg-accent/40'
                        }`}
                      >
                        <span className="w-full truncate font-medium text-foreground text-sm">
                          {user.name}
                        </span>
                        <span className="w-full truncate text-muted-foreground text-xs">
                          {t(`users:roles.${user.role}`)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="min-w-0">
            {showAdvanced ? (
              <>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-semibold text-foreground text-lg">
                      {t('users:access.advancedHeading', { name: selectedUser.name })}
                    </h2>
                    <p className="mt-0.5 text-muted-foreground text-sm">
                      {t('users:access.advancedBody')}
                    </p>
                  </div>
                  <Button type="button" variant="secondary" onClick={() => setShowAdvanced(false)}>
                    {t('users:access.backToRole')}
                  </Button>
                </div>
                <AdvancedAccessPanel
                  user={selectedUser}
                  isOn={isOn}
                  onToggle={requestToggle}
                  t={t}
                />
              </>
            ) : (
              <div className="grid min-w-0 gap-6 lg:grid-cols-2">
                <div className="min-w-0">
                  <h2 className="mb-3 font-semibold text-foreground text-lg">
                    {selectedUser.name}
                  </h2>
                  <RoleCards
                    rolePresets={rolePresets}
                    current={selectedUser.role}
                    roles={ASSIGNABLE_ROLES}
                    onSelect={(role) => requestRole(selectedUser, role)}
                    disabled={mutation.isPending}
                    t={t}
                  />

                  <button
                    type="button"
                    onClick={() => setShowAdvanced(true)}
                    className="mt-3 flex min-h-11 w-full items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="min-w-0 text-foreground text-sm">
                      {t('users:access.advancedLink')}
                    </span>
                    <span className="shrink-0 text-muted-foreground text-xs">
                      {overrideCount > 0
                        ? t('users:access.customCount', { count: overrideCount })
                        : t('users:access.noCustom')}
                    </span>
                  </button>
                </div>

                <RoleGrantSummary preset={rolePresets[selectedUser.role] ?? []} t={t} />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        heading={
          pending?.kind === 'role'
            ? t('users:access.confirm.roleHeading')
            : pending?.granting
              ? t('users:permissions.confirm.grantHeading')
              : t('users:permissions.confirm.revokeHeading')
        }
        body={
          pending === null
            ? ''
            : pending.kind === 'role'
              ? t('users:access.confirm.roleBody', {
                  name: pending.user.name,
                  role: t(`users:roles.${pending.next}`),
                })
              : t(
                  pending.granting
                    ? 'users:permissions.confirm.grantBody'
                    : 'users:permissions.confirm.revokeBody',
                  {
                    permission: t(`users:permissions.keys.${pending.permission}`),
                    name: pending.user.name,
                  },
                )
        }
        confirmLabel={
          pending?.kind === 'role'
            ? t('users:access.confirm.roleConfirm')
            : pending?.granting
              ? t('users:permissions.confirm.grantConfirm')
              : t('users:permissions.confirm.revokeConfirm')
        }
        cancelLabel={t('users:permissions.confirm.cancel')}
        confirmPending={mutation.isPending}
        onCancel={() => setPending(null)}
        onConfirm={applyPending}
      />
    </div>
  );
}
